import React, { useEffect, useState } from 'react';
import { apiClient, bidsApi, transactionsApi } from '../../utils/apiClient';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Stack,
  Chip,
  CircularProgress
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ReceiptIcon from '@mui/icons-material/Receipt';

const UserHistory = () => {
  const [bids, setBids] = useState([]);
  const [bidsPage, setBidsPage] = useState(1);
  const [bidsPages, setBidsPages] = useState(1);
  const [txs, setTxs] = useState([]);
  const [txPage, setTxPage] = useState(1);
  const [txPages, setTxPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [auctions, setAuctions] = useState([]);
  const [auctionPage, setAuctionPage] = useState(1);
  const [auctionPages, setAuctionPages] = useState(1);

  const fetchAuctions = async (page = 1) => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/auctions/auction-rooms/participation/mine', { params: { page, limit: 10 } });
      setAuctions(res.data || []);
      setAuctionPages(res.pagination?.pages || 1);
      setAuctionPage(res.pagination?.page || page);
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async (page = 1) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/bids/mine`, { params: { page, limit: 10 } });
      setBids(res.data || []);
      setBidsPages(res.pagination?.pages || 1);
      setBidsPage(res.pagination?.page || page);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page = 1) => {
    setLoading(true);
    try {
      const res = await transactionsApi.list({ page, limit: 10 });
      setTxs(res.data || []);
      setTxPages(res.pagination?.pages || 1);
      setTxPage(res.pagination?.page || page);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBids(1);
    fetchTransactions(1);
    fetchAuctions(1);
  }, []);

  const SectionHeader = ({ icon: Icon, title }) => (
    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
      <Icon color="primary" />
      <Typography variant="h5" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );

  const StyledTableContainer = ({ children }) => (
    <TableContainer 
      component={Paper} 
      sx={{ 
        mb: 4, 
        background: 'rgba(17, 24, 39, 0.7)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {children}
    </TableContainer>
  );

  const StyledTableHead = ({ children }) => (
    <TableHead sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
      {children}
    </TableHead>
  );

  const StyledTableRow = ({ children }) => (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' } }}>
      {children}
    </TableRow>
  );

  return (
    <Stack spacing={6}>
      {/* Auction Participation */}
      <Box>
        <SectionHeader icon={GavelIcon} title="Auction Participation" />
        <StyledTableContainer>
          <Table size="medium">
            <StyledTableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="center">My Bids</TableCell>
                <TableCell align="right">Last Bid</TableCell>
                <TableCell align="center">Result</TableCell>
                <TableCell align="right">Winning</TableCell>
                <TableCell align="center">Settlement</TableCell>
                <TableCell align="right">Ended</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {auctions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No auction participation found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                auctions.map(a => (
                  <StyledTableRow key={a._id}>
                    <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {a.product?.name || a._id}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{a.userBidCount}</TableCell>
                    <TableCell align="right">{a.myLastBidAmount ?? '-'}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={a.isWinner ? "Won" : "Lost"} 
                        color={a.isWinner ? "success" : "error"} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{a.winningAmount || '-'}</TableCell>
                    <TableCell align="center">
                      {a.isWinner ? (
                        <Chip 
                          label={a.settlementStatus === 'pending_cash' ? 'Cash Due' : a.settlementStatus || 'Pending'} 
                          color="warning" 
                          size="small" 
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {a.endTime ? new Date(a.endTime).toLocaleString() : ''}
                    </TableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {auctionPages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination 
                count={auctionPages} 
                page={auctionPage} 
                onChange={(e, p) => fetchAuctions(p)} 
                color="primary" 
              />
            </Box>
          )}
        </StyledTableContainer>
      </Box>

      {/* My Bids */}
      <Box>
        <SectionHeader icon={ShoppingBagIcon} title="My Bids" />
        <StyledTableContainer>
          <Table size="medium">
            <StyledTableHead>
              <TableRow>
                <TableCell>Auction</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Date</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {bids.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No bids found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bids.map(b => {
                  const formatWithMillis = (ts) => {
                    if (!ts) return '';
                    const d = new Date(ts);
                    if (isNaN(d.getTime())) return '';
                    const pad = (n, size = 2) => String(n).padStart(size, '0');
                    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(),3)}`;
                  };
                  return (
                    <StyledTableRow key={b._id}>
                      <TableCell>
                        {b.auctionRoomId?.productId?.name || b.auctionRoomId?._id}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {b.amount}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={b.status || 'active'} 
                          color={b.status === 'active' ? 'success' : 'default'} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {formatWithMillis(b.timestamp)}
                      </TableCell>
                    </StyledTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {bidsPages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination 
                count={bidsPages} 
                page={bidsPage} 
                onChange={(e, p) => fetchBids(p)} 
                color="primary" 
              />
            </Box>
          )}
        </StyledTableContainer>
      </Box>

      {/* My Purchases */}
      <Box>
        <SectionHeader icon={ReceiptIcon} title="My Purchases" />
        <StyledTableContainer>
          <Table size="medium">
            <StyledTableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Date</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {txs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No transactions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                txs.map(t => (
                  <StyledTableRow key={t._id}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{t._id}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t.tokenAmount ?? t.amount ?? t.totalAmount}
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={t.status} 
                        color={t.status === 'completed' || t.status === 'success' ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="right">
                      {t.timestamp ? new Date(t.timestamp).toLocaleString() : (t.createdAt ? new Date(t.createdAt).toLocaleString() : '')}
                    </TableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {txPages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination 
                count={txPages} 
                page={txPage} 
                onChange={(e, p) => fetchTransactions(p)} 
                color="primary" 
              />
            </Box>
          )}
        </StyledTableContainer>
      </Box>
    </Stack>
  );
};

export default UserHistory;
